import { stepLabel } from "../stepLabel.js";
import { useEffect, useMemo, useState } from "react";
import { fetchBreakdown, send, type Board, type BreakdownData } from "../api.js";
import { orderByDependency } from "../../domain/order.js";
import type { Phase, Priority } from "../../domain/types.js";
import { pruneSelection, runEach, selectableIds, toggleAll, toggleRow } from "../bulkSelect.js";

const DISCIPLINES = ["01-DFTGM", "01-DFTGE"];

/**
 * Projects, and the breakdown under each one.
 *
 * The screen's whole job is the line between the two halves. Above it is what
 * Epicor sends every morning and nobody here may edit; below it is what the
 * scheduler owns and no import may touch. If a reader cannot tell which is
 * which by looking, the screen has failed regardless of what it renders.
 */
export function Breakdown({ onOpenScenario, onChanged, focusProjectId, onFocused, board, onMove, backTo }: {
  onOpenScenario?: () => void;
  /** The schedule being viewed, for who each step is assigned to and for reassigning it. */
  board?: Board | null;
  onMove?: (
    id: string, patch: { resourceId?: string | null; queuePosition?: number; priority?: Priority },
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Set when a project was opened from another screen: Back returns there, not to the project list. */
  backTo?: { label: string; go: () => void } | null;
  /** A project to open straight away, with all its steps — the schedule sends one on a double-click. */
  focusProjectId?: string | null;
  onFocused?: () => void;
  /**
   * Called after a change is saved. The rest of the app holds its own copy of
   * the board; without this, completing or archiving here left the Backlog and
   * every other schedule screen showing what was true before.
   */
  onChanged?: () => void;
}) {
  const [data, setData] = useState<BreakdownData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "needs" | "missing">("all");
  // Find a project by its number.
  const [numberQuery, setNumberQuery] = useState("");
  // Archived projects are hidden by default: they are finished, and out of the
  // plan. Switching to Archived or All is how they are found and restored.
  const [lifecycle, setLifecycle] = useState<"active" | "archived" | "all">("active");
  const [busy, setBusy] = useState(false);
  // Projects ticked for a bulk archive / restore, and the last row ticked, which
  // a shift-click measures its range from.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);

  const load = async () => {
    try { setData(await fetchBreakdown()); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);
  // A refusal belongs to the project it happened on: opening another project, or
  // going back to the list, starts clean instead of carrying the old banner along.
  useEffect(() => { setErr(null); }, [open]);
  useEffect(() => {
    if (!focusProjectId) return;
    setOpen(focusProjectId);
    onFocused?.();
  }, [focusProjectId]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); onChanged?.(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const byProject = useMemo(() => {
    const m = new Map<string, BreakdownData["phases"]>();
    for (const p of data?.phases ?? []) {
      const list = m.get(p.projectId) ?? [];
      list.push(p);
      m.set(p.projectId, list);
    }
    for (const list of m.values()) list.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    return m;
  }, [data]);

  if (err && !data) return <div className="err">{err}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  // Mirrors domain/access.ts. Editing an existing row's plan and progress
  // fields is per-row below: a manager holds that for their own team's phases,
  // for any phase of a project type their team owns, and — matching
  // canClaimUnassigned — for an unassigned one, but only when they actually
  // manage a team; a step that names a team is that team's manager's alone.
  // Adding, reordering and applying a template (canEditSteps) is any manager of
  // a team, and an admin; archiving (canArchive) stays with the project's owner:
  // an admin, or a manager whose team owns the project's type. The server
  // decides; this only hides what it would refuse.
  const isAdmin = data.user.role === "admin";
  const norm = (t: string | null | undefined) => (t ?? "").trim().toUpperCase();
  const ownedTypes = new Set((data.user.managedProjectTypes ?? []).map(norm));
  const ownsType = (code: string | null | undefined) => norm(code) !== "" && ownedTypes.has(norm(code));
  const codeOfProject = (projectId: string) => data.projects.find((x) => x.projectId === projectId)?.productCode ?? null;
  const canEditPhase = (p: { ownerTeamId: string | null; projectId?: string; teamId?: string | null }): boolean => {
    if (isAdmin) return true;
    if (data.user.role !== "manager") return false;
    if (p.teamId) return data.user.managedTeamIds.includes(p.teamId);
    if (p.projectId && ownsType(codeOfProject(p.projectId))) return true;
    if (p.ownerTeamId === null) return data.user.managedTeamIds.length > 0;
    return data.user.managedTeamIds.includes(p.ownerTeamId);
  };
  const canEditSteps = (): boolean =>
    isAdmin || (data.user.role === "manager" && data.user.managedTeamIds.length > 0);
  const canArchive = (projectId: string): boolean =>
    isAdmin || (data.user.role === "manager" && ownsType(codeOfProject(projectId)));
  const isArchived = (p: { archivedAt?: string | null }) => Boolean(p.archivedAt);
  const activeCount = data.projects.filter((p) => !isArchived(p)).length;
  const archivedCount = data.projects.length - activeCount;
  const backCount = data.projects.filter((p) => isArchived(p) && p.backInFileAt).length;
  const shown = data.projects.filter((p) =>
    (lifecycle === "all" || (lifecycle === "archived") === isArchived(p)) && (
    filter === "needs" ? p.needsBreakdown
    : filter === "missing" ? p.importStatus === "missing_from_file"
    : true)
    && (!numberQuery.trim() || p.projectId.toLowerCase().includes(numberQuery.trim().toLowerCase())),
  );
  // Archiving and restoring are the same permission as on the project page.
  const selectable = selectableIds(shown, (id: string) => canArchive(id));
  const chosen = pruneSelection(selected, selectable);
  const toArchive = shown.filter((p) => chosen.has(p.projectId) && !isArchived(p)).map((p) => p.projectId);
  const toRestore = shown.filter((p) => chosen.has(p.projectId) && isArchived(p)).map((p) => p.projectId);
  const bulk = async (verb: "archive" | "unarchive", ids: string[]) => {
    const n = ids.length;
    const what = n === 1 ? "1 project" : `${n} projects`;
    const ok = verb === "archive"
      ? confirm(`Archive ${what}? Their steps leave the Backlog, the Gantt and the load. Nothing is deleted or completed, and you can restore them.`)
      : confirm(`Restore ${what}? Their steps return to the Backlog, the Gantt and the load.`);
    if (!ok) return;
    setBusy(true); setErr(null);
    try {
      const r = await runEach(ids, (id) => send(`/projects/${id}/${verb}`, "POST"));
      await load(); onChanged?.();
      setSelected(new Set(r.failed.map((f) => f.id)));
      setAnchor(null);
      if (r.failed.length) {
        setErr(`${verb === "archive" ? "Archived" : "Restored"} ${r.done.length}. ${r.failed.length} did not go through: `
          + r.failed.slice(0, 3).map((f) => `${f.id} (${f.message})`).join("; ")
          + (r.failed.length > 3 ? "…" : ""));
      }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const current = open ? data.projects.find((p) => p.projectId === open) ?? null : null;

  if (current) {
    return (
      <ProjectDetail
        data={data}
        project={current}
        phases={byProject.get(current.projectId) ?? []}
        isAdmin={isAdmin}
        canEditSteps={canEditSteps()}
        canArchive={canArchive(current.projectId)}
        canEditPhase={canEditPhase}
        busy={busy}
        err={err}
        act={act}
        onBack={() => { if (backTo) backTo.go(); else setOpen(null); }}
        backLabel={backTo ? `← Back to ${backTo.label}` : "← All projects"}
        board={board ?? null}
        onMove={onMove}
        canReassign={isAdmin || (data.user.role === "manager" && ownsType(current.productCode))}
        onOpenScenario={onOpenScenario}
      />
    );
  }

  return (
    <div>
      <p className="hint" style={{ marginTop: 0, marginBottom: 16 }}>
        Epicor sends the project and the date it is due. Everything under it — the steps,
        their hours, their skills, their own dates — is defined here, and the morning file
        never overwrites any of it.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 12 }}>Show</span>
        {([["active", "Active", activeCount], ["archived", "Archived", archivedCount], ["all", "All", data.projects.length]] as const)
          .map(([k, label, n]) => (
            <button key={k} className={`btn${lifecycle === k ? " on" : ""}`} onClick={() => setLifecycle(k)}>
              {label}<span className="badge">{n}</span>
              {k === "archived" && backCount > 0 && (
                <span className="badge" title="Archived projects that have reappeared in Epicor's file">{backCount} back</span>
              )}
            </button>
          ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          Project number
          <input id="projects-number" type="search" value={numberQuery} placeholder="e.g. 217385"
            onChange={(e) => setNumberQuery(e.target.value)} style={{ width: 130 }} />
        </label>
        {([["all", "Any"], ["needs", "Needs a breakdown"], ["missing", "Missing from the file"]] as const)
          .map(([k, label]) => (
            <button key={k} className={`btn${filter === k ? " on" : ""}`} onClick={() => setFilter(k)}>
              {label}
              {k === "needs" && <span className="badge">{data.projects.filter((p) => p.needsBreakdown).length}</span>}
              {k === "missing" && (
                <span className="badge">
                  {data.projects.filter((p) => p.importStatus === "missing_from_file").length}
                </span>
              )}
            </button>
          ))}
      </div>

      {chosen.size > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
          <b style={{ fontSize: 13 }}>{chosen.size} selected</b>
          {toArchive.length > 0 && (
            <button className="btn" disabled={busy} onClick={() => void bulk("archive", toArchive)}>
              Archive {toArchive.length} {toArchive.length === 1 ? "project" : "projects"}
            </button>
          )}
          {toRestore.length > 0 && (
            <button className="btn" disabled={busy} onClick={() => void bulk("unarchive", toRestore)}>
              Restore {toRestore.length} {toRestore.length === 1 ? "project" : "projects"}
            </button>
          )}
          <button className="btn" disabled={busy} onClick={() => { setSelected(new Set()); setAnchor(null); }}>Clear</button>
          <span className="muted" style={{ fontSize: 12 }}>Shift-click a box to select a range.</span>
        </div>
      )}

      <div className="scroll tall">
        <table className="qt" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                {selectable.length > 0 && (
                  <input type="checkbox" aria-label="Select all shown projects"
                    checked={selectable.every((id) => chosen.has(id))}
                    onChange={() => { setSelected(toggleAll(selectable, chosen)); setAnchor(null); }} />
                )}
              </th>
              <th>Project</th><th>Description</th><th>Customer</th><th>Product</th>
              <th>Due</th><th className="r">Steps</th><th className="r">Hours left</th><th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => {
              const steps = byProject.get(p.projectId) ?? [];
              const left = steps.reduce((n, s) => n + s.remainingHours, 0);
              return (
                <tr key={p.projectId} className="clickable" onClick={() => setOpen(p.projectId)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    {canArchive(p.projectId) && (
                      <input type="checkbox" aria-label={`Select project ${p.projectId}`}
                        checked={chosen.has(p.projectId)}
                        onChange={() => { /* handled in onClick, which carries the shift key */ }}
                        onClick={(e) => {
                          setSelected(toggleRow(selectable, chosen, p.projectId, !chosen.has(p.projectId), anchor, e.shiftKey));
                          setAnchor(p.projectId);
                        }} />
                    )}
                  </td>
                  <td className="mono"><b>{p.projectId}</b></td>
                  <td>
                    {p.description ?? "—"}
                    {isArchived(p) && (
                      <span className="chip draft" style={{ marginLeft: 10 }} title={`Archived ${p.archivedAt?.slice(0, 10) ?? ""}${p.archivedBy ? ` by ${p.archivedBy}` : ""}`}>
                        archived
                      </span>
                    )}
                    {isArchived(p) && p.backInFileAt && (
                      <span className="chip exp" style={{ marginLeft: 8 }} title={`Reappeared in Epicor's file on ${p.backInFileAt.slice(0, 10)}. Still archived — open it and restore it if the work is real.`}>
                        back in Epicor
                      </span>
                    )}
                    {p.importStatus === "missing_from_file" && (
                      <span className="chip draft" style={{ marginLeft: 8 }} title={`Last in the file on ${p.lastSeenInImport ?? "an unknown day"}`}>
                        not in the file
                      </span>
                    )}
                  </td>
                  <td className="mono">{p.customerId ?? "—"}</td>
                  <td className="mono">{p.productCode ?? "—"}</td>
                  <td className="mono">{p.committedDate ?? <span className="muted">no date</span>}</td>
                  <td className="r">
                    {steps.length === 0
                      ? <span className="chip exp">needs a breakdown</span>
                      : steps.length}
                  </td>
                  <td className="r mono">{Math.round(left)}</td>
                  <td>
                    <button className="btn" onClick={(e) => { e.stopPropagation(); setOpen(p.projectId); }}>
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
            {!shown.length && (
              <tr><td colSpan={9} className="muted">
                {lifecycle === "archived" ? "No archived projects." : "Nothing here."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectDetail({
  data, project, phases, isAdmin, canEditSteps, canArchive, canEditPhase, busy, err, act, onBack, backLabel, board, onMove,
  canReassign, onOpenScenario,
}: {
  data: BreakdownData;
  project: BreakdownData["projects"][number];
  phases: BreakdownData["phases"];
  isAdmin: boolean;
  /** May add, delete and template this project's steps. */
  canEditSteps: boolean;
  /** May archive or restore this project: an admin, or the manager of a team that owns its type. */
  canArchive: boolean;
  canEditPhase: (p: { ownerTeamId: string | null; projectId?: string; teamId?: string | null }) => boolean;
  busy: boolean;
  err: string | null;
  act: (fn: () => Promise<unknown>) => Promise<void>;
  onBack: () => void;
  backLabel: string;
  board: Board | null;
  onMove?: (
    id: string, patch: { resourceId?: string | null; queuePosition?: number; priority?: Priority },
  ) => Promise<{ ok: boolean; error?: string }>;
  /** An admin, or the manager of a team that owns this project's type. The server still decides. */
  canReassign: boolean;
  onOpenScenario?: () => void;
}) {
  // Who each step is assigned to, in the schedule being viewed (the live one or a draft).
  const assignmentOf = new Map((board?.assignments ?? []).map((a) => [a.phaseId, a]));
  const nameOf = new Map((board?.resources ?? []).map((r) => [r.resourceId, r.name]));
  const viewing = board?.scenarios.find((x) => x.scenarioId === board.scenarioId);
  const isLive = viewing?.status === "published";
  // A manager hands a step to someone on their own teams; an admin to anyone.
  const assignable = (board?.resources ?? [])
    .filter((r) => r.active && !r.isPool
      && (isAdmin || (board?.user.managedTeamIds ?? []).includes(r.teamId ?? "")))
    .sort((a, b) => a.name.localeCompare(b.name));
  const [moveErr, setMoveErr] = useState<{ phaseId: string; message: string } | null>(null);
  const reassign = async (phaseId: string, assignmentId: string, resourceId: string | null) => {
    if (!onMove) return;
    setMoveErr(null);
    const r = await onMove(assignmentId, { resourceId });
    if (!r.ok) setMoveErr({ phaseId, message: r.error ?? "Could not save that." });
  };
  // Children under their parent, indented. A cycle is broken and labeled
  // rather than left to recurse — a scheduler can point any step at any other.
  const ordered = orderByDependency(phases);

  const addStep = () =>
    act(() => send("/phases", "POST", {
      projectId: project.projectId,
      phaseCode: "New step",
      workType: "Detail",
      discipline: phases[phases.length - 1]?.discipline ?? "01-DFTGM",
      estHours: 8,
      dueOffsetDays: 0,
    }));

  const patch = (phaseId: string, body: Record<string, unknown>) =>
    act(() => send(`/phases/${phaseId}`, "PATCH", body));

  // Changing a step's team can take it off the person who holds it. The server
  // says so first and waits for a yes, so nobody loses work by surprise.
  const changeTeam = (p: Phase, teamId: string) =>
    act(async () => {
      try {
        await send(`/phases/${p.phaseId}`, "PATCH", { teamId });
      } catch (e) {
        const msg = (e as Error).message;
        if (!/back to the pool/.test(msg) || !confirm(`${msg} Continue?`)) throw e;
        await send(`/phases/${p.phaseId}`, "PATCH", { teamId, confirm: true });
      }
    });

  const teamName = new Map(data.teams.map((t) => [t.teamId, t.name]));

  const remove = (p: Phase) =>
    act(async () => {
      try {
        await send(`/phases/${p.phaseId}`, "DELETE");
      } catch (e) {
        const msg = (e as Error).message;
        if (!/under way/.test(msg) || !confirm(`${msg} Delete it anyway?`)) throw e;
        await send(`/phases/${p.phaseId}`, "DELETE", { force: true });
      }
    });

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <button className="btn" onClick={onBack}>{backLabel}</button>
        <span className="spacer" />
        {project.archivedAt && (
          <span className="chip draft" title={`Archived ${project.archivedAt.slice(0, 10)}${project.archivedBy ? ` by ${project.archivedBy}` : ""}`}>
            archived
          </span>
        )}
        {project.archivedAt && project.backInFileAt && (
          <span className="chip exp" title="Reappeared in Epicor's file after being absent. Restore it if the work is real.">
            back in Epicor since {project.backInFileAt.slice(0, 10)}
          </span>
        )}
        {canEditSteps && !project.archivedAt && (
          <button className="btn" disabled={busy} onClick={() => act(() =>
            send(`/projects/${project.projectId}/apply-template`, "POST"))}>
            Apply the template
          </button>
        )}
        {canArchive && (
          <button className="btn" disabled={busy} onClick={() => {
            if (project.archivedAt) {
              void act(() => send(`/projects/${project.projectId}/unarchive`, "POST"));
              return;
            }
            const open = phases.filter((p) => p.remainingHours > 0).length;
            const warn = open
              ? `${project.projectId} still has ${open} step${open === 1 ? "" : "s"} with work left. `
              : "";
            if (!confirm(`${warn}Archive ${project.projectId}? It leaves the Backlog and the load, and can be restored from the Archived list.`)) return;
            void act(() => send(`/projects/${project.projectId}/archive`, "POST"));
          }}>
            {project.archivedAt ? "Restore project" : "Archive project"}
          </button>
        )}
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      {/* Above the line: Epicor's, and nobody's to edit here. */}
      <div className="run-sec">
        <h3>
          From Epicor
          <span className="run-sec-note">
            {project.lastSeenInImport
              ? `last seen in the file on ${project.lastSeenInImport}`
              : "not yet seen in a file"}
          </span>
        </h3>
        <dl className="facts wide">
          {/* Each pair wrapped so it's one grid item, not two — otherwise a
              label can land as the last cell in a row with its value
              wrapping to the next one, same fix as RunLog's Facts. */}
          <div><dt>Project</dt><dd className="mono">{project.projectId}</dd></div>
          <div><dt>Description</dt><dd>{project.description ?? "—"}</dd></div>
          <div><dt>Customer</dt><dd className="mono">{project.customerId ?? "—"}</dd></div>
          <div><dt>Sales rep</dt><dd>{project.salesRepName ?? "—"}</dd></div>
          <div><dt>Product code</dt><dd className="mono">{project.productCode ?? "—"}</dd></div>
          <div>
            <dt>Value</dt>
            <dd className="mono">
              {project.valueUsd === null ? "—" : `$${project.valueUsd.toLocaleString()}`}
            </dd>
          </div>
          <div><dt>Status</dt><dd>{project.projectStatus ?? "—"}</dd></div>
          <div><dt>Order type</dt><dd>{project.orderType ?? "—"}</dd></div>
          <div><dt>Due date</dt><dd className="mono">{project.committedDate ?? "no date"}</dd></div>
        </dl>
        <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
          These arrive with the morning file and are read-only here. The due date is the
          latest phase date Epicor holds for the project.
        </p>
      </div>

      {/* Below the line: the scheduler's. */}
      <div className="run-sec">
        <h3>
          The breakdown
          <span className="run-sec-note">
            {phases.length} step{phases.length === 1 ? "" : "s"} · {Math.round(phases.reduce((n, s) => n + s.remainingHours, 0) * 10) / 10} h left · yours to define
          </span>
        </h3>

        {!phases.length && (
          <p className="muted">
            No steps yet. Apply the template for this product code, or add one by hand.
          </p>
        )}

        {!!phases.length && (
          <div className="scroll tall">
            <table className="qt" style={{ minWidth: board ? 1760 : 1610 }}>
              <thead>
                <tr>
                  <th className="r">#</th><th>Step</th><th>Description</th><th>Team</th>{board && <th>Assigned to</th>}<th>Waits on</th><th>Work type</th><th>Disc</th>
                  <th className="r">Est</th><th className="r">Act</th>
                  <th>Skill</th><th className="r">Lvl</th>
                  <th>Due</th><th>Offset</th><th>State</th><th>Completed</th><th></th>
                </tr>
              </thead>
              <tbody>
                {ordered.map(({ phase: p, depth, cycle }) => (
                  <tr key={p.phaseId} style={{ opacity: busy ? 0.6 : 1 }}>
                    <td className="r mono">{p.seq ?? "—"}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                     paddingLeft: depth * 18 }}>
                        {depth > 0 && (
                          <span className="dep-mark" title="waits on the step above">└</span>
                        )}
                        <input defaultValue={p.phaseCode ?? ""} disabled={!canEditPhase(p)}
                          onBlur={(e) => e.target.value !== p.phaseCode && void patch(p.phaseId, { phaseCode: e.target.value })} />
                        {cycle && (
                          <span className="dep-cycle" title="these steps wait on each other">loop</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <input defaultValue={p.description ?? ""} disabled={!canEditPhase(p)}
                        aria-label={`Description, ${p.phaseCode ?? "step"}`} style={{ minWidth: 160 }}
                        onBlur={(e) => e.target.value !== (p.description ?? "")
                          && void patch(p.phaseId, { description: e.target.value || null })} />
                    </td>
                    <td>
                      {(isAdmin || data.user.role === "manager") ? (
                        <select value={p.teamId ?? ""} aria-label={`Team, ${p.phaseCode ?? "step"}`}
                          disabled={busy}
                          title="The team that owns this step. Change it to hand the step to another team; its manager then assigns it."
                          onChange={(e) => e.target.value && void changeTeam(p, e.target.value)}>
                          {!p.teamId && <option value="">— no team —</option>}
                          {data.teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
                        </select>
                      ) : (
                        <span className={p.teamId ? "" : "muted"}>{p.teamId ? teamName.get(p.teamId) ?? "—" : "—"}</span>
                      )}
                    </td>
                    {board && (() => {
                      const a = assignmentOf.get(p.phaseId);
                      const who = a?.resourceId ? nameOf.get(a.resourceId) ?? a.resourceId : null;
                      const canMove = canReassign && !isLive && !!a && !!onMove && (isAdmin || !a.frozen);
                      return (
                        <td>
                          {canMove && a ? (
                            <select value={a.resourceId ?? ""} aria-label={`Assigned to, ${p.phaseCode ?? "step"}`}
                              onChange={(e) => void reassign(p.phaseId, a.assignmentId, e.target.value || null)}>
                              <option value="">— unassigned —</option>
                              {a.resourceId && !assignable.some((r) => r.resourceId === a.resourceId) && (
                                <option value={a.resourceId}>{who}</option>
                              )}
                              {/* A step that names a team goes to someone on it; to give it to anyone else, change its team first. */}
                              {assignable.filter((r) => !p.teamId || r.teamId === p.teamId)
                                .map((r) => <option key={r.resourceId} value={r.resourceId}>{r.name}</option>)}
                            </select>
                          ) : (
                            <span className={who ? "" : "muted"}
                              title={canReassign && a?.frozen && !isAdmin ? "Work in progress can only be moved by an admin." : undefined}>
                              {who ?? "unassigned"}
                            </span>
                          )}
                          {moveErr?.phaseId === p.phaseId && (
                            <div className="err" style={{ marginTop: 4, fontSize: 12 }}>{moveErr.message}</div>
                          )}
                        </td>
                      );
                    })()}
                    <td>
                      <select value={p.dependsOnPhaseId ?? ""} disabled={!canEditPhase(p)}
                        onChange={(e) => void patch(p.phaseId, { dependsOnPhaseId: e.target.value || null })}>
                        <option value="">— nothing —</option>
                        {phases.filter((o) => o.phaseId !== p.phaseId).map((o) => (
                          <option key={o.phaseId} value={o.phaseId}>
                            {o.seq}. {o.phaseCode ?? stepLabel(o, o.phaseId)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={p.workType} disabled={!canEditPhase(p)}
                        onChange={(e) => void patch(p.phaseId, { workType: e.target.value })}>
                        {data.workTypes.filter((w) => w.active || w.code === p.workType).map((w) => (
                          <option key={w.code} value={w.code}>{w.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={p.discipline ?? ""} disabled={!canEditPhase(p)}
                        onChange={(e) => void patch(p.phaseId, { discipline: e.target.value || null })}>
                        <option value="">—</option>
                        {DISCIPLINES.map((d) => <option key={d} value={d}>{d.replace("01-DFTG", "")}</option>)}
                      </select>
                    </td>
                    <td className="r">
                      <input className="num" type="number" step="0.5" min="0" defaultValue={p.estHours} disabled={!canEditPhase(p)}
                        onBlur={(e) => Number(e.target.value) !== p.estHours && void patch(p.phaseId, { estHours: Number(e.target.value) })} />
                    </td>
                    <td className="r">
                      <input className="num" type="number" step="0.5" min="0" defaultValue={p.actHours} disabled={!canEditPhase(p)}
                        onBlur={(e) => Number(e.target.value) !== p.actHours && void patch(p.phaseId, { actHours: Number(e.target.value) })} />
                    </td>
                    <td>
                      <select value={p.skillId ?? ""} disabled={!canEditPhase(p)}
                        onChange={(e) => void patch(p.phaseId, {
                          skillId: e.target.value || null,
                          minProficiency: e.target.value ? (p.minProficiency ?? 2) : null,
                        })}>
                        <option value="">— anyone —</option>
                        {data.skills.map((s) => (
                          <option key={s.skillId} value={s.skillId}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="r">
                      {p.skillId ? (
                        <select value={p.minProficiency ?? 1} disabled={!canEditPhase(p)}
                          onChange={(e) => void patch(p.phaseId, { minProficiency: Number(e.target.value) })}>
                          <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                        </select>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>
                      <input type="date" defaultValue={p.reqDueDate ?? ""} disabled={!canEditPhase(p)}
                        onBlur={(e) => e.target.value !== (p.reqDueDate ?? "") && void patch(p.phaseId, { reqDueDate: e.target.value || null })} />
                    </td>
                    <td>
                      <input className="num" type="number" step="1" placeholder="pinned"
                        defaultValue={p.dueOffsetDays ?? ""} disabled={!canEditPhase(p)}
                        title="Days from the project due date (negative is earlier): this step's Due date follows the project. Blank means the step's own Due date is pinned and no import will move it."
                        onBlur={(e) => {
                          const v = e.target.value === "" ? null : Number(e.target.value);
                          if (v !== p.dueOffsetDays) void patch(p.phaseId, { dueOffsetDays: v });
                        }} />
                    </td>
                    <td>
                      <select value={p.workflowState} disabled={!canEditPhase(p)}
                        onChange={(e) => void patch(p.phaseId, { workflowState: e.target.value })}>
                        {data.workflowStates.map((s) => (
                          <option key={s.stateId} value={s.stateId}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/* Stamped when the step is marked Complete; a manager or an admin may set an earlier day. */}
                      {data.workflowStates.some((s) => s.stateId === p.workflowState && s.isTerminal) ? (
                        <input type="date" defaultValue={p.completedOn ?? ""} max={data.today}
                          disabled={!canEditPhase(p)}
                          title="The day this step was completed. Today or earlier."
                          onBlur={(e) => e.target.value && e.target.value !== (p.completedOn ?? "")
                            && void patch(p.phaseId, { completedOn: e.target.value })} />
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>
                      {canEditSteps && canEditPhase(p) && (
                        <button className="btn" disabled={busy} onClick={() => void remove(p)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {board && canReassign && isLive && (
          <p className="hint" style={{ marginTop: 10 }}>
            This is the live schedule, so steps are shown but not reassigned from here. Start a draft
            schedule (the bar at the top), then change <b>Assigned to</b> and publish it when it is right.
          </p>
        )}

        {canEditSteps && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button className="btn on" disabled={busy} onClick={() => void addStep()}>Add a step</button>
            <span className="muted" style={{ fontSize: 12 }}>
              An <b>offset</b> sets the step's due date as days from the <b>project's</b> due date
              (negative is earlier), and it moves when the project date does. Clear it to
              pin the step's own <b>Due</b> date, which then stays put. Setting a Due date
              by hand clears the offset. <b>Waits on</b> puts a step under the one it follows, and the
              optimizer will not start it until that one finishes.
            </span>
          </div>
        )}
      </div>

      {onOpenScenario && (
        <p className="muted" style={{ fontSize: 12 }}>
          Changing hours or dates here changes what the next solve is given. It does not move
          anything on the published schedule until you re-solve and publish.
        </p>
      )}
    </div>
  );
}
